import React, { FC, useEffect, useState, useCallback } from "react";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import style from "./style.module.css";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";
import BarcodeScannerModal from "../../Components/BarcodeScannerModal";
import Pagination from "../../Components/Pagination";
import Backdrop from "../../Components/Backdrop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { usePrintReceipt } from "../../utils/usePrintReceipt";
import { enqueuePendingSale, loadPendingSales, submitPendingSale, SaleSubmission } from "../../utils/offlineSalesQueue";
import { parseApiError } from "../../utils/parseApiError";
import { getPosSettings } from "../../utils/posSettings";

const PRODUCT_PAGE_SIZE = 100;

interface CartItem {
  product_id: number;
  product_name: string;
  product_image: string;
  unit_price: number;
  quantity: number;
  weight?: string;
  // Undiscounted list price — only set when it differs from unit_price (an
  // active product/category discount applied). Purely for the receipt's
  // "Item Discount" line. See docs/discount-pricing-plan.md.
  original_price?: number;
}

const CheckoutPage: FC = () => {
  const [cookies] = useCookies();
  const snack = useSnackbar();

  const [products, setProducts] = useState<any[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalItems, setProductTotalItems] = useState(0);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  // Mobile-only: the order-summary/cart panel collapses behind a sticky
  // bottom bar under 768px (see style.module.css's .mobileCartBar /
  // .rightExpanded) so a cashier isn't forced to scroll past the whole
  // product grid to reach the total or the Complete Sale button.
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "card" | "online">("cash");
  const [amountTendered, setAmountTendered] = useState("");
  const [orderType, setOrderType] = useState<"pickup" | "delivery">("pickup");
  // docs/store-channel-access-control-plan.md, Step 8 — the acting staff's
  // own store's channel_type, resolved the same way DeliverySlotsPage does
  // (GET /store, only acted on when it resolves to exactly one store — a
  // multi-store or zero-store Admin is ambiguous, so both pills stay
  // enabled rather than guessing which store this sale belongs to). null =
  // still loading, or ambiguous — never disables Delivery in that state.
  const [checkoutStoreChannelType, setCheckoutStoreChannelType] = useState<string | null>(null);
  const [checkoutStoreName, setCheckoutStoreName] = useState<string>(() => {
    try {
      return localStorage.getItem("pos_store_name") || "";
    } catch {
      return "";
    }
  });
  const deliveryDisabled = checkoutStoreChannelType !== null && checkoutStoreChannelType !== "pickup_and_delivery";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isPrinting: isPrintingReceipt, print: doPrintReceipt } = usePrintReceipt();
  const [completedOrderId, setCompletedOrderId] = useState<number | null>(null);

  // Structural, not imperative: any current or future code path that sets
  // completedOrderId (completeSale, a refund/reprint flow, restoring a
  // completed sale after a refresh, ...) gets the mobile cart panel
  // expanded/collapsed for free, without needing to separately remember to
  // call setMobileCartOpen itself — the two can't drift out of sync since
  // there's only one place deriving one from the other.
  useEffect(() => {
    setMobileCartOpen(completedOrderId !== null);
  }, [completedOrderId]);

  useEffect(() => {
    authenticatedFetch(`${API_BASE}/store`, { method: "GET" }, cookies.auth)
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((data) => {
        const list = data.data || data || [];
        if (list.length === 1) setCheckoutStoreChannelType(list[0].channel_type ?? "pickup_and_delivery");
        if (Array.isArray(list) && list.length > 0) {
          const matchedStore = (cookies.auth?.store_id ? list.find((s: any) => s.store_id === cookies.auth.store_id) : null) || list[0];
          if (matchedStore?.store_name) {
            setCheckoutStoreName(matchedStore.store_name);
            try {
              localStorage.setItem("pos_store_name", matchedStore.store_name);
            } catch {}
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A staff member could click "Delivery" while the store fetch above is
  // still in flight (deliveryDisabled defaults to false/unknown) — if it
  // then resolves to a store that doesn't support delivery, drop back to
  // pickup rather than leaving an already-invalid selection standing.
  useEffect(() => {
    if (deliveryDisabled && orderType === "delivery") setOrderType("pickup");
  }, [deliveryDisabled, orderType]);

  interface SaleSnapshot {
    orderId: number;
    items: CartItem[];
    subtotal: number;
    discountAmount: number;
    couponName: string;
    total: number;
    paymentMethod: string;
    amountTendered: number;
    changeDue: number;
    orderType: string;
    timestamp: Date;
    cashier: string;
    isOfflineSale?: boolean;
    offlineRef?: string;
    storeName?: string;
  }
  const [lastSale, setLastSale] = useState<SaleSnapshot | null>(null);

  interface ParkedSale {
    parked_sale_id: number;
    label: string | null;
    created_at: string;
    cart_snapshot: {
      items: CartItem[];
      order_type: "pickup" | "delivery";
      couponCode?: string;
      appliedCoupon?: any;
      discountAmount?: number;
    };
  }
  const [heldSales, setHeldSales] = useState<ParkedSale[]>([]);
  const [showHeldModal, setShowHeldModal] = useState(false);
  const [showParkModal, setShowParkModal] = useState(false);
  const [parkLabel, setParkLabel] = useState("");
  const [isParking, setIsParking] = useState(false);
  const [isLoadingHeldSales, setIsLoadingHeldSales] = useState(false);

  // Offline-resilient checkout (docs/pos-cashier-operations-plan.md PR 5).
  // navigator.onLine is checked up front so a cashier who's already offline
  // never even attempts the live 4-call sequence; a mid-submit connectivity
  // drop is caught separately, in completeSale's catch block below.
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSalesCount, setPendingSalesCount] = useState(() => loadPendingSales().length);
  const [showOfflineConfirm, setShowOfflineConfirm] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    // Same-tab signal PendingSalesSyncManager (App.tsx) fires on every
    // queue mutation — keeps this page's badge in sync with sales it
    // successfully synced in the background while this page stayed mounted.
    const refresh = () => setPendingSalesCount(loadPendingSales().length);
    window.addEventListener("pendingSalesChanged", refresh);
    return () => window.removeEventListener("pendingSalesChanged", refresh);
  }, []);

  const subtotal = cart.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  const total = Math.max(0, subtotal - discountAmount);
  const tendered = parseFloat(amountTendered) || 0;
  const changeDue = paymentMethod === "cash" ? tendered - total : 0;

  // Debounce so typing doesn't refetch on every keystroke.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // /product is paginated server-side now — search moves server-side too
  // (checkout needs to find any product in the catalog, not just the first
  // page of it), so `products` is already whatever currently matches.
  const filteredProducts = products;

  // Reset back to page 1 whenever the search term changes, so a new search
  // doesn't leave the grid stranded on a page number that no longer exists.
  useEffect(() => {
    setProductPage(1);
  }, [debouncedSearchTerm]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(productPage), limit: String(PRODUCT_PAGE_SIZE) });
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      const res = await authenticatedFetch(`${API_BASE}/product?${params}`, { method: "GET" }, cookies.auth);
      if (res.ok) {
        const result = await res.json();
        const data = result.data || [];
        const totalPages = result.totalPages || 1;
        const total = result.total || 0;
        setProducts(data);
        setProductTotalPages(totalPages);
        setProductTotalItems(total);
        if (!debouncedSearchTerm && productPage === 1 && data.length > 0) {
          try {
            localStorage.setItem("pos_cached_catalog", JSON.stringify({ data, totalPages, total }));
          } catch {
            // ignore storage errors
          }
        }
      } else {
        throw new Error("Backend not reachable");
      }
    } catch (e) {
      console.error(e);
      // Attempt to hydrate from previously cached catalog if offline/unreachable
      let hydratedFromCache = false;
      try {
        const cachedRaw = localStorage.getItem("pos_cached_catalog");
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          if (cached && Array.isArray(cached.data) && cached.data.length > 0) {
            setProducts(cached.data);
            setProductTotalPages(cached.totalPages || 1);
            setProductTotalItems(cached.total || cached.data.length);
            hydratedFromCache = true;
          }
        }
      } catch {
        // ignore cache parse errors
      }

      if (!hydratedFromCache) {
        setProducts([]);
        setProductTotalPages(1);
        setProductTotalItems(0);
      }
    }
  }, [cookies.auth, productPage, debouncedSearchTerm]);

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_BASE}/coupon?page=1&limit=100`, { method: "GET" }, cookies.auth);
      if (res.ok) {
        const result = await res.json();
        setCoupons(result.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [cookies.auth]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const addToCart = (product: any) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.product_id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.product_id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          product_name: product.product_name,
          product_image: product.product_image,
          weight: product.weight || (product.unit_value && product.unit ? `${product.unit_value} ${product.unit}` : undefined),
          // Defaults to the active discount's price when one applies (see
          // docs/discount-pricing-plan.md) — the backend doesn't enforce this
          // on the staff channel (a discretionary in-person override is
          // legitimate), this is just the correct starting point.
          unit_price: product.discounted_price ?? parseFloat(product.price),
          original_price: product.discounted_price != null ? parseFloat(product.price) : undefined,
          quantity: 1,
        },
      ];
    });
  };

  const updateQty = (product_id: number, delta: number) => {
    setCart((prev) =>
      prev
        .map((i) => (i.product_id === product_id ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeFromCart = (product_id: number) => {
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  };

  const addProductByBarcode = async (rawBarcode: string) => {
    const barcode = rawBarcode.trim();
    if (!barcode) return;
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/product/barcode/${encodeURIComponent(barcode)}`,
        { method: "GET" },
        cookies.auth
      );
      if (res.ok) {
        const product = await res.json();
        addToCart(product);
        snack.onResponse({ message: `Added: ${product.product_name}`, status: 200 });
      } else {
        snack.onResponse({ message: "No product found for this barcode", status: 400 });
      }
    } catch {
      snack.onResponse({ message: "Barcode lookup failed", status: 400 });
    }
    setBarcodeInput("");
  };

  const handleBarcodeKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    await addProductByBarcode(barcodeInput);
  };

  const applyCoupon = () => {
    const now = new Date();
    const found = coupons.find(
      (c) =>
        c.coupon_name?.toLowerCase() === couponCode.trim().toLowerCase() &&
        new Date(c.valid_from) <= now &&
        new Date(c.valid_until) >= now
    );
    if (!found) {
      snack.onResponse({ message: "Invalid or expired coupon", status: 400 });
      setAppliedCoupon(null);
      setDiscountAmount(0);
      return;
    }
    setAppliedCoupon(found);
    const value = parseFloat(found.coupon_value) || 0;
    const discount =
      found.coupon_type === "percentage" ? (subtotal * value) / 100 : value;
    setDiscountAmount(discount);
    snack.onResponse({ message: `Coupon "${found.coupon_name}" applied!`, status: 200 });
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setDiscountAmount(0);
  };

  const resetSale = () => {
    setCart([]);
    setCouponCode("");
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setAmountTendered("");
    setCompletedOrderId(null); // also collapses the mobile cart panel back down, via the effect above
    setLastSale(null);
  };

  // Abandons an in-progress cart before payment — pure client-side reset, no
  // API call, since nothing's been committed yet at this point (checkout
  // only starts writing anything once "Complete Sale" is pressed). Distinct
  // from resetSale, which also clears the post-completion success banner —
  // this only ever renders pre-completion (see the button's guard below).
  const clearCart = () => {
    setCart([]);
    setCouponCode("");
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setAmountTendered("");
  };

  const fetchHeldSales = useCallback(async () => {
    setIsLoadingHeldSales(true);
    try {
      const res = await authenticatedFetch(`${API_BASE}/parkedsale`, { method: "GET" }, cookies.auth);
      if (res.ok) {
        const result = await res.json();
        setHeldSales(result.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingHeldSales(false);
    }
  }, [cookies.auth]);

  useEffect(() => {
    fetchHeldSales();
  }, [fetchHeldSales]);

  // Park the in-progress cart, freeing up the register for the next
  // customer — nothing about the cart's server-side representation exists
  // yet at this point (checkout only writes anything once "Complete Sale"
  // is pressed), so this is the pre-payment equivalent of Clear Cart, just
  // persisted instead of discarded.
  const parkSale = async () => {
    if (cart.length === 0) return;
    setIsParking(true);
    try {
      const res = await authenticatedFetch(
        `${API_BASE}/parkedsale`,
        {
          method: "POST",
          body: JSON.stringify({
            label: parkLabel.trim() || undefined,
            cart_snapshot: { items: cart, order_type: orderType, couponCode, appliedCoupon, discountAmount },
          }),
        },
        cookies.auth
      );
      if (!res.ok) {
        snack.onResponse({ message: await parseApiError(res, "Failed to park sale"), status: res.status });
        return;
      }
      clearCart();
      setShowParkModal(false);
      setParkLabel("");
      await fetchHeldSales();
      snack.onResponse({ message: "Sale parked", status: 200 });
    } catch (err: any) {
      snack.onResponse({ message: err instanceof Error ? err.message : "Failed to park sale", status: 400 });
    } finally {
      setIsParking(false);
    }
  };

  // Rehydrates the cart from a parked sale's snapshot, then re-checks each
  // item's current price/stock against the live catalog rather than
  // silently trusting the (possibly stale) snapshot — prices/discounts can
  // change between when a sale was parked and when it's resumed.
  const resumeHeldSale = async (parkedSaleId: number) => {
    try {
      const res = await authenticatedFetch(`${API_BASE}/parkedsale/${parkedSaleId}/resume`, { method: "PATCH" }, cookies.auth);
      if (!res.ok) {
        snack.onResponse({ message: await parseApiError(res, "Failed to resume parked sale"), status: res.status });
        return;
      }
      const parked: ParkedSale = await res.json();
      const snapshot = parked.cart_snapshot;
      setCart(snapshot.items);
      setOrderType(snapshot.order_type);
      setCouponCode(snapshot.couponCode || "");
      setAppliedCoupon(snapshot.appliedCoupon || null);
      setDiscountAmount(snapshot.discountAmount || 0);
      setShowHeldModal(false);
      await fetchHeldSales();
      snack.onResponse({ message: "Sale resumed", status: 200 });

      const changedItems: string[] = [];
      await Promise.all(
        snapshot.items.map(async (item) => {
          try {
            const productRes = await authenticatedFetch(`${API_BASE}/product/${item.product_id}`, { method: "GET" }, cookies.auth);
            if (!productRes.ok) return;
            const product = await productRes.json();
            const currentPrice = product.discounted_price != null ? product.discounted_price : parseFloat(product.price);
            if (currentPrice !== item.unit_price) {
              changedItems.push(item.product_name);
            }
          } catch (e) {
            // Best-effort check — a failed lookup for one item shouldn't block the resume.
          }
        })
      );
      if (changedItems.length > 0) {
        snack.onResponse({
          message: `Price may have changed since this was parked: ${changedItems.join(", ")}. Review before completing the sale.`,
          status: 400,
        });
      }
    } catch (err: any) {
      snack.onResponse({ message: err instanceof Error ? err.message : "Failed to resume parked sale", status: 400 });
    }
  };

  const discardHeldSale = async (parkedSaleId: number) => {
    try {
      const res = await authenticatedFetch(`${API_BASE}/parkedsale/${parkedSaleId}`, { method: "DELETE" }, cookies.auth);
      if (!res.ok) {
        snack.onResponse({ message: await parseApiError(res, "Failed to discard parked sale"), status: res.status });
        return;
      }
      await fetchHeldSales();
      snack.onResponse({ message: "Parked sale discarded", status: 200 });
    } catch (err: any) {
      snack.onResponse({ message: err instanceof Error ? err.message : "Failed to discard parked sale", status: 400 });
    }
  };

  // The single payload shape both a live submit and an offline-queued one
  // are built from — used by completeSale (submits it immediately via
  // submitPendingSale) and queueCurrentSale (persists it via
  // enqueuePendingSale for the background sync manager to submit later via
  // that same submitPendingSale). 'pending' was the old pre-lifecycle-
  // migration status name (see 0010_order_status_lifecycle.sql) — a Pickup
  // sale is already complete at checkout (the customer has the goods in
  // hand), so it goes straight to 'delivered' instead of sitting in the
  // confirming/packing/etc. pipeline meant for orders that still need to be
  // fulfilled. A Delivery order placed via POS (e.g. a phone order staff
  // enters) hasn't actually been delivered yet, so it still starts at
  // 'confirming' like a normal order — submitPendingSale derives this from
  // order_type itself, not passed explicitly here.
  const buildSaleSubmission = (): SaleSubmission => ({
    order_type: orderType,
    total_amount: total,
    // Persisted so a later Generate Bill (OrderPage.tsx) can display the
    // coupon-style discount that was applied at checkout, rather than
    // always showing 0 — total_amount itself is already correct either way
    // since POS orders aren't touched by the customer-channel total_amount
    // self-heal in orderItemController.js.
    discount_amount: discountAmount,
    created_by: cookies.auth?.user_id ?? null,
    items: cart.map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      product_image: item.product_image,
      unit_price: item.unit_price,
      quantity: item.quantity,
    })),
    payment_method: paymentMethod,
    amount: parseFloat(total.toFixed(2)),
    amount_tendered: paymentMethod === "cash" ? tendered : null,
    change_given: paymentMethod === "cash" ? parseFloat(Math.max(0, changeDue).toFixed(2)) : null,
  });

  // Queues the current sale for background sync (offlineSalesQueue.ts) and
  // resets the cart exactly like a completed sale would — the cashier can
  // keep ringing up the next customer immediately, same as CheckoutPage's
  // own resetSale/clearCart already do after a live submit.
  const queueCurrentSale = () => {
    const sale = buildSaleSubmission();
    const queued = enqueuePendingSale(sale);
    setPendingSalesCount(loadPendingSales().length);
    setShowOfflineConfirm(false);

    // Assign temporary order ID and capture snapshot so cashier can print receipt immediately
    const digitsOnly = queued.id.replace(/\D/g, "");
    const offlineOrderId = parseInt(digitsOnly.slice(-6) || String(Date.now()).slice(-6), 10);
    setLastSale({
      orderId: offlineOrderId,
      items: [...cart],
      subtotal,
      discountAmount,
      couponName: appliedCoupon?.coupon_name ?? "",
      total,
      paymentMethod,
      amountTendered: tendered,
      changeDue: paymentMethod === "cash" ? Math.max(0, changeDue) : 0,
      orderType,
      timestamp: new Date(),
      cashier: `${cookies.auth?.first_name ?? ""} ${cookies.auth?.last_name ?? ""}`.trim() || "Staff",
      isOfflineSale: true,
      offlineRef: String(offlineOrderId),
      storeName: checkoutStoreName || localStorage.getItem("pos_store_name") || undefined,
    });
    setCompletedOrderId(offlineOrderId);
    setCart([]);
    setCouponCode("");
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setAmountTendered("");
    snack.onResponse({ message: `Offline sale complete — Order #${offlineOrderId} (queued to sync)`, status: 200 });
  };

  const completeSale = async () => {
    if (cart.length === 0) { snack.onResponse({ message: "Cart is empty", status: 400 }); return; }
    if (paymentMethod === "cash" && tendered < total) {
      snack.onResponse({ message: "Amount tendered is less than the total", status: 400 });
      return;
    }
    if (!isOnline) {
      setShowOfflineConfirm(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitPendingSale(buildSaleSubmission(), cookies.auth);
      if (!result.ok) {
        // A rejected fetch() (TypeError, surfaced by submitPendingSale as
        // networkError) means the request never reached the server —
        // connectivity dropped mid-submit, not a real server rejection (a
        // 4xx/5xx already comes back as result.ok === false with a message,
        // handled by the throw below, not this branch). Cart items already
        // committed by an earlier step in this same attempt are left as-is —
        // same pre-existing gap this checkout flow has always had (no
        // cross-request transaction, see this page's own top-of-file note);
        // queuing just gives the cashier a second full attempt rather than a
        // dead end.
        if (result.networkError) {
          setShowOfflineConfirm(true);
          return;
        }
        throw new Error(result.message || "Sale failed");
      }
      const orderId = result.orderId!;

      setLastSale({
        orderId,
        items: [...cart],
        subtotal,
        discountAmount,
        couponName: appliedCoupon?.coupon_name ?? "",
        total,
        paymentMethod,
        amountTendered: tendered,
        changeDue: paymentMethod === "cash" ? Math.max(0, changeDue) : 0,
        orderType,
        timestamp: new Date(),
        cashier: `${cookies.auth?.first_name ?? ""} ${cookies.auth?.last_name ?? ""}`.trim() || "Staff",
        storeName: checkoutStoreName || localStorage.getItem("pos_store_name") || undefined,
      });
      setCompletedOrderId(orderId); // also expands the mobile cart panel, via the effect above — otherwise the
      // success confirmation, Print Receipt, and New Sale buttons would render but stay hidden behind the
      // sticky bar (which would itself misleadingly show "0 items" once the cart is cleared below)
      setCart([]);
      setCouponCode("");
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setAmountTendered("");
      snack.onResponse({ message: `Sale complete — Order #${orderId}`, status: 200 });
      fetchProducts();
    } catch (err: any) {
      snack.onResponse({ message: err instanceof Error ? err.message : "Sale failed", status: 400 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fmt = (n: number) => formatCurrency(n);

  const printReceipt = async () => {
    if (!lastSale) return;
    const s = lastSale;
    try {
      const result = await doPrintReceipt("checkout", () => {
        const catalogDiscountAmount = s.items.reduce(
          (sum, item) => sum + (item.original_price != null ? (item.original_price - item.unit_price) * item.quantity : 0),
          0
        );
        const configuredStoreName = getPosSettings().storeName?.trim();
        const resolvedStoreName = configuredStoreName || s.storeName || checkoutStoreName || localStorage.getItem("pos_store_name") || undefined;
        return {
          orderId: s.orderId,
          timestamp: s.timestamp,
          cashier: s.cashier,
          orderType: s.orderType,
          items: s.items.map((item) => ({
            product_name: item.product_name,
            unit_price: item.unit_price,
            quantity: item.quantity,
          })),
          subtotal: s.subtotal,
          discountAmount: s.discountAmount,
          couponName: s.couponName,
          catalogDiscountAmount,
          total: s.total,
          paymentMethod: s.paymentMethod,
          amountTendered: s.amountTendered,
          changeDue: s.changeDue,
          isOfflineSale: s.isOfflineSale,
          offlineRef: s.offlineRef,
          storeName: resolvedStoreName,
        };
      });
      if (result && !result.ok) {
        snack.onResponse({ message: result.message || "Failed to print the receipt", status: 400 });
      }
    } catch (error) {
      // doPrintReceipt/printReceipt can throw (e.g. a device error not
      // already handled internally) — without this, it was an unhandled
      // promise rejection: the button re-enables via usePrintReceipt's own
      // finally, but the cashier saw no receipt and no explanation why.
      console.error("Error printing receipt:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Error printing the receipt", status: 500 });
    }
  };

  return (
    <div className={style.wrapper}>
      {/* Header */}
      <div className={style.header}>
        <h1 className={style.title}>Checkout</h1>
        <div className={style.orderTypePill}>
          <button
            className={orderType === "pickup" ? style.pillActive : style.pill}
            onClick={() => setOrderType("pickup")}
          >
            Pickup
          </button>
          <button
            className={deliveryDisabled ? style.pillDisabled : orderType === "delivery" ? style.pillActive : style.pill}
            onClick={() => { if (!deliveryDisabled) setOrderType("delivery"); }}
            disabled={deliveryDisabled}
            title={deliveryDisabled ? "Not available for this store" : undefined}
          >
            Delivery
          </button>
        </div>
        <button
          className={style.heldSalesBtn}
          onClick={() => { setShowHeldModal(true); fetchHeldSales(); }}
        >
          Held Sales {heldSales.length > 0 ? `(${heldSales.length})` : ""}
        </button>
      </div>

      {(!isOnline || pendingSalesCount > 0) && (
        <div className={!isOnline ? style.offlineBanner : style.pendingSyncBanner}>
          {!isOnline
            ? "No connection — sales will be queued and synced automatically once you're back online."
            : `${pendingSalesCount} sale${pendingSalesCount === 1 ? "" : "s"} pending sync…`}
        </div>
      )}

      <div className={style.body}>
        {/* LEFT — product search */}
        <div className={style.left}>
          <div className={style.scanBar}>
            <span className={style.scanLabel}>Barcode Scanner</span>
            <input
              className={style.barcodeInput}
              placeholder="Scan barcode and press Enter…"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={handleBarcodeKeyDown}
            />
            <button
              type="button"
              className={style.cameraScanButton}
              onClick={() => setShowScannerModal(true)}
              title="Scan with camera"
            >
              <FontAwesomeIcon icon={faCamera} />
            </button>
          </div>
          {showScannerModal && (
            <BarcodeScannerModal
              onScan={(value) => {
                setShowScannerModal(false);
                addProductByBarcode(value);
              }}
              onClose={() => setShowScannerModal(false)}
            />
          )}

          <input
            className={style.searchInput}
            placeholder="Search products by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className={style.productGrid}>
            {filteredProducts.map((p) => (
              <div
                key={p.product_id}
                className={`${style.productCard} ${p.quantity === 0 ? style.outOfStock : ""}`}
              >
                <div className={style.productName}>
                  {p.product_name}
                  {(p.weight || (p.unit_value && p.unit ? `${p.unit_value} ${p.unit}` : null)) && (
                    <span style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', fontWeight: 400 }}>
                      {p.weight || `${p.unit_value} ${p.unit}`}
                    </span>
                  )}
                </div>
                <div className={style.productPrice}>
                  {p.discounted_price != null ? (
                    <>
                      <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: 6 }}>
                        {fmt(parseFloat(p.price))}
                      </span>
                      <span style={{ color: '#059669', fontWeight: 600 }}>{fmt(p.discounted_price)}</span>
                    </>
                  ) : (
                    fmt(parseFloat(p.price))
                  )}
                </div>
                <div className={style.productStock}>
                  {p.quantity > 0 ? `Stock: ${p.quantity}` : "Out of stock"}
                </div>
                <button
                  className={style.addBtn}
                  onClick={() => addToCart(p)}
                  disabled={p.quantity === 0}
                >
                  + Add
                </button>
              </div>
            ))}
            {filteredProducts.length === 0 && (
              <div className={style.emptySearch}>No products found</div>
            )}
          </div>

          <Pagination page={productPage} totalPages={productTotalPages} onPageChange={setProductPage} totalItems={productTotalItems} />
        </div>

        {/* RIGHT — order panel. On mobile this and .mobileCartBar below are
            both children of .mobileCartDock (display:contents on desktop,
            so it's a no-op there and .right keeps its normal place in this
            flex row) — a fixed-position flex column on mobile, so the bar
            sits at the bottom and the expanded panel stacks directly above
            it via normal layout, with no hardcoded offset between them. */}
        <div className={style.mobileCartDock}>
        <div className={`${style.right} ${mobileCartOpen ? style.rightExpanded : ""}`}>
          {/* Cart */}
          <div className={style.section}>
            <h2 className={style.sectionTitle}>Order Summary</h2>
            {cart.length === 0 ? (
              <div className={style.emptyCart}>Scan or select products to add to the order</div>
            ) : (
              <div className={style.cartItems}>
                {cart.map((item) => (
                  <div key={item.product_id} className={style.cartItem}>
                    <div className={style.cartItemName}>
                      {item.product_name}
                      {item.weight && <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '6px', fontWeight: 400 }}>({item.weight})</span>}
                    </div>
                    <div className={style.cartItemControls}>
                      <button className={style.qtyBtn} onClick={() => updateQty(item.product_id, -1)}>−</button>
                      <span className={style.qtyValue}>{item.quantity}</span>
                      <button className={style.qtyBtn} onClick={() => updateQty(item.product_id, 1)}>+</button>
                    </div>
                    <div className={style.cartItemPrice}>{fmt(item.unit_price * item.quantity)}</div>
                    <button className={style.removeBtn} onClick={() => removeFromCart(item.product_id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coupon */}
          <div className={style.section}>
            {appliedCoupon ? (
              <div className={style.appliedCoupon}>
                <span>🏷 {appliedCoupon.coupon_name}</span>
                <button className={style.removeCouponBtn} onClick={removeCoupon}>Remove</button>
              </div>
            ) : (
              <div className={style.couponRow}>
                <input
                  className={style.couponInput}
                  placeholder="Coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                />
                <button className={style.couponBtn} onClick={applyCoupon}>Apply</button>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className={style.totals}>
            <div className={style.totalRow}>
              <span>Subtotal</span>
              <span>{fmt(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className={`${style.totalRow} ${style.discountRow}`}>
                <span>Discount</span>
                <span>− {fmt(discountAmount)}</span>
              </div>
            )}
            <div className={`${style.totalRow} ${style.grandTotal}`}>
              <span>Total</span>
              <span>{fmt(total)}</span>
            </div>
          </div>

          {/* Payment */}
          <div className={style.section}>
            <h2 className={style.sectionTitle}>Payment Method</h2>
            <div className={style.methodRow}>
              {(["cash", "card", "online"] as const).map((m) => (
                <button
                  key={m}
                  className={paymentMethod === m ? style.methodActive : style.methodBtn}
                  onClick={() => setPaymentMethod(m)}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>

            {paymentMethod === "cash" && (
              <>
                <div className={style.tenderedRow}>
                  <label>Amount Tendered</label>
                  <input
                    className={style.tenderedInput}
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    value={amountTendered}
                    onChange={(e) => setAmountTendered(e.target.value)}
                  />
                </div>
                <div className={style.quickCashRow}>
                  <button
                    type="button"
                    className={style.quickCashBtn}
                    onClick={() => setAmountTendered(String(total))}
                  >
                    Exact
                  </button>
                  {[100, 500, 1000, 5000].map((denom) => (
                    <button
                      key={denom}
                      type="button"
                      className={style.quickCashBtn}
                      onClick={() => {
                        const current = parseFloat(amountTendered) || 0;
                        setAmountTendered(String(current + denom));
                      }}
                    >
                      +{denom}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={style.quickCashClearBtn}
                    onClick={() => setAmountTendered("")}
                  >
                    Clear
                  </button>
                </div>
                <div className={`${style.totalRow} ${changeDue >= 0 ? style.changeRow : style.shortfallRow}`}>
                  <span>{changeDue >= 0 ? "Change Due" : "Shortfall"}</span>
                  <span>{fmt(Math.abs(changeDue))}</span>
                </div>
              </>
            )}
          </div>

          {/* Complete button */}
          <button
            className={style.completeBtn}
            onClick={completeSale}
            disabled={isSubmitting || cart.length === 0}
          >
            {isSubmitting ? "Processing…" : "Complete Sale"}
          </button>

          {completedOrderId === null && cart.length > 0 && (
            <div className={style.cartActionsRow}>
              <button
                className={style.parkSaleBtn}
                onClick={() => setShowParkModal(true)}
                disabled={isSubmitting}
              >
                Park Sale
              </button>
              <button
                className={style.clearCartBtn}
                onClick={clearCart}
                disabled={isSubmitting}
              >
                Clear Cart
              </button>
            </div>
          )}

          {completedOrderId !== null && (
            <div className={style.successBanner}>
              <span>✓ Sale complete — Order #{completedOrderId}</span>
              <div style={{ display: "flex", gap: "0.45rem" }}>
                <button className={style.printBtn} onClick={printReceipt} disabled={isPrintingReceipt}>
                  {isPrintingReceipt ? "Printing…" : "🖨 Print Receipt"}
                </button>
                <button className={style.newSaleBtn} onClick={resetSale}>New Sale</button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile-only: collapsed cart lives behind this sticky bar so the
            product grid above never buries the total / Complete Sale button. */}
        <div className={style.mobileCartBar} onClick={() => setMobileCartOpen((o) => !o)}>
          <span className={style.mobileCartBarSummary}>
            {cart.length} item{cart.length === 1 ? "" : "s"} · {fmt(total)}
          </span>
          <span className={style.mobileCartBarToggle}>{mobileCartOpen ? "▼ Hide" : "▲ View Cart"}</span>
        </div>
        </div>

        {mobileCartOpen && (
          <Backdrop zIndex={90} onClick={() => setMobileCartOpen(false)} />
        )}
      </div>

      {/* Held Sales panel */}
      {showHeldModal && (
        <div className={style.heldModalOverlay}>
          <div className={style.heldModal}>
            <h2>Held Sales</h2>
            {isLoadingHeldSales ? (
              <p className={style.heldEmptyText}>Loading…</p>
            ) : heldSales.length === 0 ? (
              <p className={style.heldEmptyText}>No parked sales.</p>
            ) : (
              <div className={style.heldSaleList}>
                {heldSales.map((h) => (
                  <div key={h.parked_sale_id} className={style.heldSaleRow}>
                    <div className={style.heldSaleInfo}>
                      <span className={style.heldSaleLabel}>{h.label || `Parked sale #${h.parked_sale_id}`}</span>
                      <span className={style.heldSaleMeta}>
                        {h.cart_snapshot.items.length} item{h.cart_snapshot.items.length === 1 ? "" : "s"} ·{" "}
                        {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className={style.heldSaleActions}>
                      <button className={style.resumeBtn} onClick={() => resumeHeldSale(h.parked_sale_id)}>Resume</button>
                      <button className={style.discardBtn} onClick={() => discardHeldSale(h.parked_sale_id)}>Discard</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className={style.modalButtons}>
              <button onClick={() => setShowHeldModal(false)} className={style.cancelBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Park Sale modal */}
      {showParkModal && (
        <div className={style.heldModalOverlay}>
          <div className={style.heldModal}>
            <h2>Park This Sale</h2>
            <p className={style.heldEmptyText}>
              Optionally label it so it's easy to find later (e.g. "Blue shirt customer").
            </p>
            <input
              className={style.parkLabelInput}
              type="text"
              placeholder="Label (optional)"
              value={parkLabel}
              onChange={(e) => setParkLabel(e.target.value)}
            />
            <div className={style.modalButtons}>
              <button onClick={() => { setShowParkModal(false); setParkLabel(""); }} className={style.cancelBtn}>Cancel</button>
              <button onClick={parkSale} className={style.newSaleBtn} disabled={isParking}>
                {isParking ? "Parking…" : "Park Sale"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline / connectivity-drop confirmation */}
      {showOfflineConfirm && (
        <div className={style.heldModalOverlay}>
          <div className={style.heldModal}>
            <h2>No Connection</h2>
            <p className={style.heldEmptyText}>
              This sale will be queued on this device and synced automatically once you're back
              online. Continue?
            </p>
            <div className={style.modalButtons}>
              <button onClick={() => setShowOfflineConfirm(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={queueCurrentSale} className={style.newSaleBtn}>Queue Sale</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
